import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  InputGroup,
  Card,
  Dropdown,
  Pagination,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import {
  FaTrashAlt,
  FaEdit,
  FaPlus,
  FaSearch,
  FaEllipsisV,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaEye,
  FaUserTie,
  FaUsers,
  FaTrophy,
  FaChartLine,
} from "react-icons/fa";
import { APICall } from "../Utls/Api";

const Department = () => {
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewDepartment, setViewDepartment] = useState(null);

  // Leaderboard states
  const [allEmployeesLeaderboard, setAllEmployeesLeaderboard] = useState([]);
  const [departmentEmployeesLeaderboard, setDepartmentEmployeesLeaderboard] =
    useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  // Data states
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Department form data
  const [departmentData, setDepartmentData] = useState({
    name: "",
  });

  // Current department being edited/deleted
  const [currentDepartment, setCurrentDepartment] = useState(null);

  // Table interaction states
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(5);

  // Show alert message
  const showAlert = (message, variant = "success") => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ ...alert, show: false }), 3000);
  };

  // Fetch departments on component mount
  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await APICall("auth/department/all", "GET");
      setDepartments(response.departments || []);
    } catch (error) {
      showAlert("Failed to fetch departments", "danger");
      console.error("Error fetching departments:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch leaderboard data
  const fetchLeaderboardData = async (departmentId) => {
    try {
      setLeaderboardLoading(true);

      // Fetch all employees leaderboard
      const allEmployeesResponse = await APICall(
        "dashboard/employee/leaderborad",
        "GET"
      );
      if (allEmployeesResponse.status) {
        setAllEmployeesLeaderboard(
          allEmployeesResponse?.data?.employeeLeaderboard || []
        );
      }

      // Fetch department-specific employees leaderboard
      const departmentResponse = await APICall(
        `dashboard/department-employees/${departmentId}`,
        "GET"
      );
      setDepartmentEmployeesLeaderboard(
        departmentResponse.data.employees || []
      );
    } catch (error) {
      showAlert("Failed to fetch leaderboard data", "danger");
      console.error("Error fetching leaderboard data:", error);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  // Sort departments
  const sortedDepartments = useMemo(() => {
    let sortableItems = [...departments];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [departments, sortConfig]);

  // Filter departments based on search term
  const filteredDepartments = useMemo(() => {
    return sortedDepartments.filter((department) =>
      department.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sortedDepartments, searchTerm]);

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredDepartments.slice(
    indexOfFirstRow,
    indexOfLastRow
  );
  const totalPages = Math.ceil(filteredDepartments.length / rowsPerPage);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ms-1" />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="ms-1" />
    ) : (
      <FaSortDown className="ms-1" />
    );
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAllRows = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentRows.map((row) => row._id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleDeleteClick = (department) => {
    setCurrentDepartment(department);
    setShowDeleteModal(true);
  };

  const handleEditClick = (department) => {
    setCurrentDepartment(department);
    setDepartmentData({
      name: department.name || "",
    });
    setShowEditModal(true);
  };

  const handleViewClick = async (department) => {
    setViewDepartment(department);
    setShowViewModal(true);
    await fetchLeaderboardData(department._id);
  };

  const handleDelete = async () => {
    try {
      await APICall(
        `auth/department/delete/${currentDepartment._id}`,
        "DELETE"
      );
      showAlert("Department deleted successfully");
      fetchDepartments();
      setSelectedRows((prev) =>
        prev.filter((rowId) => rowId !== currentDepartment._id)
      );
      setShowDeleteModal(false);
    } catch (error) {
      showAlert("Failed to delete department", "danger");
      console.error("Error deleting department:", error);
    }
  };

  const handleBulkDelete = () => {
    if (
      window.confirm(
        `Are you sure you want to delete ${selectedRows.length} departments?`
      )
    ) {
      // Implement bulk delete logic here
      showAlert(`${selectedRows.length} departments deleted`, "success");
      setSelectedRows([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDepartmentData({ ...departmentData, [name]: value });
  };

  const handleAddSubmit = async () => {
    try {
      setLoading(true);
      await APICall("auth/department/create", "POST", departmentData);
      showAlert("Department created successfully");
      setShowAddModal(false);
      setDepartmentData({ name: "" });
      fetchDepartments();
    } catch (error) {
      showAlert(`Failed to create department: ${error.message}`, "danger");
      console.error("Error creating department:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    try {
      setLoading(true);
      const data = {
        id: currentDepartment._id,
        name: departmentData.name,
      };
      await APICall(`auth/department/update/${currentDepartment._id}`, "PUT", data);
      showAlert("Department updated successfully");
      setShowEditModal(false);
      setDepartmentData({ name: "" });
      fetchDepartments();
    } catch (error) {
      showAlert(`Failed to update department: ${error.message}`, "danger");
      console.error("Error updating department:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderRankBadge = (rank) => {
    let variant = "secondary";
    if (rank === 1) variant = "warning";
    else if (rank === 2) variant = "info";
    else if (rank === 3) variant = "danger";

    return (
      <Badge bg={variant} pill className="px-3 py-2">
        {rank}
      </Badge>
    );
  };

  return (
    <div className="p-4">
      {/* Alert Notification */}
      {alert.show && (
        <Alert
          variant={alert.variant}
          className="fade-in-alert"
          onClose={() => setAlert({ ...alert, show: false })}
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1 fw-bold text-orange">Department Management</h2>
          <p className="text-muted mb-0">
            Manage your organization's departments and view performance metrics
          </p>
        </div>
        <Button
          variant="orange"
          onClick={() => setShowAddModal(true)}
          disabled={loading}
          className="d-flex align-items-center rounded-pill px-4"
        >
          <FaPlus className="me-2" />
          Add Department
        </Button>
      </div>

      {/* Search and Filter Bar */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-3 bg-light rounded">
          <div className="d-flex justify-content-between align-items-center">
            <InputGroup style={{ width: "300px" }}>
              <InputGroup.Text className="bg-white border-end-0">
                <FaSearch className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search departments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-start-0 ps-0"
              />
            </InputGroup>

            <div className="d-flex align-items-center gap-2">
              {selectedRows.length > 0 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="d-flex align-items-center rounded-pill"
                >
                  <FaTrashAlt className="me-1" />
                  Delete ({selectedRows.length})
                </Button>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Departments Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="orange" />
              <p className="mt-3">Loading departments...</p>
            </div>
          ) : (
            <>
              <div className="rounded">
                <Table hover className="mb-0">
                  <thead className="bg-orange text-white">
                    <tr>
                      <th className="ps-4" style={{ width: "50px" }}>
                        <Form.Check
                          type="checkbox"
                          onChange={handleSelectAllRows}
                          checked={
                            selectedRows.length === currentRows.length &&
                            currentRows.length > 0
                          }
                        />
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => requestSort("name")}
                      >
                        <div className="d-flex align-items-center">
                          Department Name
                          {getSortIcon("name")}
                        </div>
                      </th>
                      <th className="text-end pe-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.length > 0 ? (
                      currentRows.map((department) => (
                        <tr
                          key={department._id}
                          className={
                            selectedRows.includes(department._id)
                              ? "table-active"
                              : ""
                          }
                        >
                          <td className="ps-4">
                            <Form.Check
                              type="checkbox"
                              checked={selectedRows.includes(department._id)}
                              onChange={() => handleSelectRow(department._id)}
                            />
                          </td>
                          <td>
                            <div className="fw-semibold">{department.name}</div>
                            <small className="text-muted">
                              ID: {department._id.substring(0, 8)}...
                            </small>
                          </td>
                          <td className="text-end pe-4">
                            <Dropdown>
                              <Dropdown.Toggle
                                variant="light"
                                size="sm"
                                className="px-2 rounded-pill"
                              >
                                <FaEllipsisV />
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end">
                                <Dropdown.Item
                                  onClick={() => handleEditClick(department)}
                                  className="d-flex align-items-center"
                                >
                                  <FaEdit className="me-2 text-orange" />
                                  Edit
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleDeleteClick(department)}
                                  className="d-flex align-items-center"
                                >
                                  <FaTrashAlt className="me-2 text-danger" />
                                  Delete
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleViewClick(department)}
                                  className="d-flex align-items-center"
                                >
                                  <FaEye className="me-2 text-info" />
                                  View Details
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-5">
                          <h5>No departments found</h5>
                          <p className="text-muted">
                            Try adjusting your search or add a new department
                          </p>
                          <Button
                            variant="orange"
                            onClick={() => setShowAddModal(true)}
                            className="mt-2"
                          >
                            <FaPlus className="me-2" />
                            Add Department
                          </Button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {filteredDepartments.length > rowsPerPage && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <div className="text-muted">
                    Showing {indexOfFirstRow + 1} to{" "}
                    {Math.min(indexOfLastRow, filteredDepartments.length)} of{" "}
                    {filteredDepartments.length} entries
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                    />
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Add Department Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h4 fw-bold">Add New Department</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Department Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter department name"
                name="name"
                value={departmentData.name}
                onChange={handleChange}
                disabled={loading}
                className="py-2"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="light"
            onClick={() => setShowAddModal(false)}
            disabled={loading}
            className="rounded-pill px-4"
          >
            Cancel
          </Button>
          <Button
            variant="orange"
            onClick={handleAddSubmit}
            disabled={loading || !departmentData.name}
            className="rounded-pill px-4"
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Adding...
              </>
            ) : (
              "Add Department"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Department Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h4 fw-bold">Edit Department</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Department Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter department name"
                name="name"
                value={departmentData.name}
                onChange={handleChange}
                disabled={loading}
                className="py-2"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="light"
            onClick={() => setShowEditModal(false)}
            disabled={loading}
            className="rounded-pill px-4"
          >
            Cancel
          </Button>
          <Button
            variant="orange"
            onClick={handleEditSubmit}
            disabled={loading || !departmentData.name}
            className="rounded-pill px-4"
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Updating...
              </>
            ) : (
              "Update Department"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete department{" "}
            <strong>{currentDepartment?.name}</strong>?
          </p>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="light"
            onClick={() => setShowDeleteModal(false)}
            className="rounded-pill px-4"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={loading}
            className="rounded-pill px-4"
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* TRUE FULLSCREEN MODAL - View Department Leaderboards */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        fullscreen={true}
        backdrop="static"
        keyboard={true}
        className="fullscreen-modal position-absolute top-0"
        style={{ zIndex: 10000 }}
      >
        {/* Fullscreen Header */}
        <div className="fullscreen-header">
          <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center py-3">
              <div className="d-flex align-items-center">
                <FaUserTie className="me-3 text-white" size={28} />
                <div>
                  <h3 className="mb-0 text-white fw-bold">
                    {viewDepartment?.name} Department
                  </h3>
                  <p className="mb-0 text-white-50">
                    Employee Performance Dashboard
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Button
                  variant="outline-light"
                  onClick={() => fetchLeaderboardData(viewDepartment?._id)}
                  disabled={leaderboardLoading}
                  className="d-flex align-items-center"
                >
                  <FaChartLine className="me-2" />
                  Refresh
                </Button>
                <Button
                  variant="light"
                  onClick={() => setShowViewModal(false)}
                  className="d-flex align-items-center px-4"
                >
                  <span className="me-2">✕</span>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Fullscreen Body */}
        <div className="fullscreen-body">
          {leaderboardLoading ? (
            <div className="fullscreen-loading">
              <div className="text-center">
                <Spinner
                  animation="border"
                  variant="orange"
                  style={{ width: "4rem", height: "4rem" }}
                />
                <h2 className="mt-4 text-orange">Loading Performance Data</h2>
                <p className="text-muted fs-5">
                  Fetching employee rankings and analytics...
                </p>
              </div>
            </div>
          ) : (
            <div className="container-fluid h-100 py-4">
              {/* Top Statistics Row */}
              <Row className="mb-4">
                <Col xl={3} lg={6} md={6} sm={12} className="mb-3">
                  <Card className="stat-card border-0 shadow-sm h-100">
                    <Card.Body className="text-center p-4">
                      <div className="stat-icon mb-3">
                        <FaUsers size={40} className="text-orange" />
                      </div>
                      <h2 className="fw-bold text-orange mb-1">
                        {departmentEmployeesLeaderboard.length}
                      </h2>
                      <p className="text-muted mb-0 fs-6">Total Employees</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={3} lg={6} md={6} sm={12} className="mb-3">
                  <Card className="stat-card border-0 shadow-sm h-100">
                    <Card.Body className="text-center p-4">
                      <div className="stat-icon mb-3">
                        <FaTrophy size={40} className="text-warning" />
                      </div>
                      <h2 className="fw-bold text-warning mb-1">
                        {
                          departmentEmployeesLeaderboard.filter(
                            (emp) => emp.rank <= 10
                          ).length
                        }
                      </h2>
                      <p className="text-muted mb-0 fs-6">Top 10 Performers</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={3} lg={6} md={6} sm={12} className="mb-3">
                  <Card className="stat-card border-0 shadow-sm h-100">
                    <Card.Body className="text-center p-4">
                      <div className="stat-icon mb-3">
                        <FaChartLine size={40} className="text-success" />
                      </div>
                      <h2 className="fw-bold text-success mb-1">
                        {departmentEmployeesLeaderboard.length > 0
                          ? Math.round(
                              departmentEmployeesLeaderboard.reduce(
                                (sum, emp) => sum + emp.totalExpertise,
                                0
                              ) / departmentEmployeesLeaderboard.length
                            )
                          : 0}
                      </h2>
                      <p className="text-muted mb-0 fs-6">Average Score</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={3} lg={6} md={6} sm={12} className="mb-3">
                  <Card className="stat-card border-0 shadow-sm h-100">
                    <Card.Body className="text-center p-4">
                      <div className="stat-icon mb-3">
                        <FaUserTie size={40} className="text-info" />
                      </div>
                      <h2 className="fw-bold text-info mb-1">
                        {departmentEmployeesLeaderboard.length > 0
                          ? Math.max(
                              ...departmentEmployeesLeaderboard.map(
                                (emp) => emp.totalExpertise
                              )
                            )
                          : 0}
                      </h2>
                      <p className="text-muted mb-0 fs-6">Highest Score</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Main Content Row */}
              <Row className="flex-grow-1">
                {/* Global Leaderboard */}
                <Col lg={6} className="mb-4">
                  <Card className="border-0 shadow h-100">
                    <Card.Header className="bg-orange text-white py-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <FaUsers className="me-2" size={20} />
                          <h5 className="mb-0 fw-bold">Global Rankings</h5>
                        </div>
                        <Badge
                          bg="light"
                          text="orange"
                          pill
                          className="px-3 py-2"
                        >
                          {allEmployeesLeaderboard.length} employees
                        </Badge>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="table-container">
                        <Table hover className="mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th
                                className="ps-4 py-3"
                                style={{ width: "80px" }}
                              >
                                Rank
                              </th>
                              <th
                                className="py-3"
                                style={{ minWidth: "200px" }}
                              >
                                Employee
                              </th>
                              <th className="py-3" style={{ width: "140px" }}>
                                Department
                              </th>
                              <th
                                className="text-end pe-4 py-3"
                                style={{ width: "100px" }}
                              >
                                Score
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {allEmployeesLeaderboard.length > 0 ? (
                              allEmployeesLeaderboard.map((employee, index) => (
                                <tr
                                  key={index}
                                  className={
                                    employee.department === viewDepartment?.name
                                      ? "table-orange"
                                      : ""
                                  }
                                >
                                  <td className="ps-4 py-3">
                                    {renderRankBadge(employee.rank)}
                                  </td>
                                  <td className="py-3">
                                    <div className="fw-semibold fs-6">
                                      {employee.employeeName}
                                    </div>
                                    <small className="text-muted">
                                      {employee.email || "No email"}
                                    </small>
                                  </td>
                                  <td className="py-3">
                                    <Badge
                                      bg={
                                        employee.department ===
                                        viewDepartment?.name
                                          ? "orange"
                                          : "secondary"
                                      }
                                      className="px-2 py-1"
                                    >
                                      {employee.department}
                                    </Badge>
                                  </td>
                                  <td className="text-end pe-4 py-3">
                                    <span className="fw-bold fs-5 text-orange">
                                      {employee.totalExpertise}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="4" className="text-center py-5">
                                  <div className="empty-state">
                                    <FaUsers
                                      size={60}
                                      className="text-muted mb-3 opacity-50"
                                    />
                                    <h4 className="text-muted">
                                      No Global Data
                                    </h4>
                                    <p className="text-muted">
                                      Unable to load company-wide rankings
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Department Leaderboard */}
                <Col lg={6} className="mb-4">
                  <Card className="border-0 shadow h-100">
                    <Card.Header className="bg-orange text-white py-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <FaTrophy className="me-2" size={20} />
                          <h5 className="mb-0 fw-bold">
                            {viewDepartment?.name} Team
                          </h5>
                        </div>
                        <Badge
                          bg="light"
                          text="orange"
                          pill
                          className="px-3 py-2"
                        >
                          {departmentEmployeesLeaderboard.length} employees
                        </Badge>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="table-container">
                        <Table hover className="mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th
                                className="ps-4 py-3"
                                style={{ width: "80px" }}
                              >
                                Rank
                              </th>
                              <th
                                className="py-3"
                                style={{ minWidth: "180px" }}
                              >
                                Employee
                              </th>
                              <th
                                className="py-3"
                                style={{ minWidth: "160px" }}
                              >
                                Supervisor
                              </th>
                              <th
                                className="text-end pe-4 py-3"
                                style={{ width: "100px" }}
                              >
                                Score
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {departmentEmployeesLeaderboard.length > 0 ? (
                              departmentEmployeesLeaderboard.map(
                                (employee, index) => (
                                  <tr key={index}>
                                    <td className="ps-4 py-3">
                                      {renderRankBadge(
                                        employee.rank || index + 1
                                      )}
                                    </td>
                                    <td className="py-3">
                                      <div className="fw-semibold fs-6">
                                        {employee.employeeName}
                                      </div>
                                      <small className="text-muted">
                                        {employee.email || "No email"}
                                      </small>
                                    </td>
                                    <td className="py-3">
                                      {employee.supervisorName ? (
                                        <div>
                                          <div className="fw-semibold text-dark">
                                            {employee.supervisorName}
                                          </div>
                                          <small className="text-muted">
                                            {employee.supervisorEmail || ""}
                                          </small>
                                        </div>
                                      ) : (
                                        <span className="text-muted fst-italic">
                                          No supervisor
                                        </span>
                                      )}
                                    </td>
                                    <td className="text-end pe-4 py-3">
                                      <span className="fw-bold fs-5 text-orange">
                                        {employee.totalExpertise}
                                      </span>
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td colSpan="4" className="text-center py-5">
                                  <div className="empty-state">
                                    <FaTrophy
                                      size={60}
                                      className="text-muted mb-3 opacity-50"
                                    />
                                    <h4 className="text-muted">
                                      No Department Data
                                    </h4>
                                    <p className="text-muted">
                                      No employees found in{" "}
                                      {viewDepartment?.name}
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </Modal>

      {/* Enhanced Fullscreen Styles */}
      <style jsx>{`
        /* Custom Orange Theme Variables */
        :root {
          --orange-primary: #ff6b35;
          --orange-secondary: #ff8c42;
          --orange-light: #ffa366;
          --orange-dark: #e55a2b;
          --orange-gradient: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
        }

        /* Orange Theme Classes */
        .text-orange {
          color: var(--orange-primary) !important;
        }

        .bg-orange {
          background-color: var(--orange-primary) !important;
        }

        .btn-orange {
          background-color: var(--orange-primary);
          border-color: var(--orange-primary);
          color: white;
        }

        .btn-orange:hover,
        .btn-orange:focus,
        .btn-orange:active {
          background-color: var(--orange-dark);
          border-color: var(--orange-dark);
          color: white;
        }

        .btn-outline-orange {
          color: var(--orange-primary);
          border-color: var(--orange-primary);
          background-color: transparent;
        }

        .btn-outline-orange:hover,
        .btn-outline-orange:focus,
        .btn-outline-orange:active {
          background-color: var(--orange-primary);
          border-color: var(--orange-primary);
          color: white;
        }

        .badge.bg-orange {
          background-color: var(--orange-primary) !important;
          color: white;
        }

        .spinner-border.text-orange {
          color: var(--orange-primary) !important;
        }

        .text-orange {
          color: var(--orange-primary) !important;
        }

        /* Fullscreen Modal Styles */
        .fullscreen-modal .modal-dialog {
          margin: 0 !important;
          max-width: 100% !important;
          height: 100vh !important;
          width: 100vw !important;
        }

        .fullscreen-modal .modal-content {
          height: 100vh !important;
          border: none !important;
          border-radius: 0 !important;
          display: flex !important;
          flex-direction: column !important;
        }

        .fullscreen-header {
          background: var(--orange-gradient) !important;
          flex-shrink: 0;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          z-index: 1000;
        }

        .fullscreen-body {
          flex: 1 1 auto;
          overflow-y: auto;
          background: #f8f9fa;
          display: flex;
          flex-direction: column;
        }

        .fullscreen-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: 400px;
        }

        .stat-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border-radius: 12px !important;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }

        .stat-icon {
          padding: 15px;
          border-radius: 50%;
          background: rgba(255, 107, 53, 0.1);
          display: inline-block;
        }

        .table-container {
          height: calc(100vh - 400px);
          min-height: 400px;
          overflow-y: auto;
          border-radius: 0 0 8px 8px;
        }

        .table-container::-webkit-scrollbar {
          width: 8px;
        }

        .table-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        .table-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 10px;
        }

        .table-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        .table-orange {
          background-color: rgba(255, 107, 53, 0.15) !important;
          border-left: 4px solid var(--orange-primary) !important;
        }

        .sticky-top {
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
        }

        .empty-state {
          padding: 40px 20px;
        }

        /* Bootstrap variant overrides for orange theme */
        .btn-check:checked + .btn-orange,
        .btn-orange:first-child:active,
        :not(.btn-check) + .btn-orange:active {
          background-color: var(--orange-dark);
          border-color: var(--orange-dark);
        }

        .pagination .page-item.active .page-link {
          background-color: var(--orange-primary);
          border-color: var(--orange-primary);
        }

        .pagination .page-link:hover {
          color: var(--orange-dark);
          background-color: rgba(255, 107, 53, 0.1);
          border-color: var(--orange-light);
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .fullscreen-header .d-flex {
            flex-direction: column;
            gap: 1rem;
          }

          .table-container {
            height: calc(100vh - 300px);
            min-height: 300px;
          }

          .stat-card {
            margin-bottom: 1rem;
          }
        }

        /* Animation */
        .fade-in-alert {
          animation: fadeIn 0.3s;
        }

        .cursor-pointer {
          cursor: pointer;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Department;